import { useRefreshTick } from "../lib/useAutoRefresh";
import { useEffect, useState, Fragment } from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import { api, apiErr, pdfUrl } from "../lib/api";
import { money, FREQ_LABELS, fmtDateTime } from "../lib/format";
import { ArrowLeft, FileDown, Banknote, Mail, FileText, BookText } from "lucide-react";
import { toast } from "sonner";
import { useSort, SortBtn } from "../lib/sortable";
import RunWorkflowPanel from "../components/RunWorkflowPanel";
import { RunDirectDeposit } from "../components/DirectDepositPanel";
import { useAuth } from "../context/AuthContext";

const DEP = { submitted: ["Deposit submitted", "border-amber-500 bg-amber-50 text-amber-800"], settled: ["Deposited", "border-emerald-600 bg-emerald-50 text-emerald-700"],
  returned: ["Deposit returned", "border-red-500 bg-red-50 text-red-700"], failed: ["Deposit failed", "border-red-500 bg-red-50 text-red-700"], pending: ["Deposit pending", "border-border text-muted-foreground"], voided: ["Deposit voided", "border-border text-muted-foreground"] };
function DepositBadge({ d, id }) {
  if (!d) return null;
  const [label, cls] = DEP[d.status] || [d.status, "border-border"];
  return <span data-testid={`deposit-badge-${id}`} title={d.response_desc || ""} className={`ml-2 text-[9px] font-mono uppercase border px-1 py-0.5 ${cls}`}>{label} ····{d.last4}{d.status === "settled" && d.settle_date ? ` · ${String(d.settle_date).slice(0, 10)}` : ""}</span>;
}
import { useClientPerms } from "../lib/useClientPerms";
import PayrollExceptionsPanel from "../components/PayrollExceptionsPanel";
import { ClosedNoticeDialog } from "../components/ClosedNoticeDialog";
import { PayslipEmailDialog } from "../components/PayslipEmailDialog";

const BTN = "inline-flex items-center gap-2 border border-black bg-white px-4 py-2.5 text-sm font-medium hover:bg-black hover:text-white transition-colors disabled:opacity-50";
const SUMMARY = "cursor-pointer list-none flex items-center justify-between border border-border bg-white px-5 py-3 font-bold tracking-tight hover:bg-neutral-50";

const PS_A = {
  name: (p) => p.employee_name || "", gross: (p) => p.gross || 0, fed: (p) => p.federal_tax || 0, on: (p) => p.provincial_tax || 0,
  cpp: (p) => p.cpp || 0, cpp2: (p) => p.cpp2 || 0, ei: (p) => p.ei || 0, net: (p) => p.net_pay || 0,
  ytd_gross: (p) => p.ytd_gross || 0, ytd_net: (p) => p.ytd_net || 0,
};

export default function RunDetail() {
  const { runId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const base = location.pathname.startsWith("/client") ? "/client" : "";
  const [data, setData] = useState(null);
  const [company, setCompany] = useState(null);
  const companyName = company?.name || "";
  const { user } = useAuth();
  const isStaff = user?.role === "super_admin";
  const canRunEdit = useClientPerms().canEdit("run_payroll");
  const [expanded, setExpanded] = useState({});
  const tick = useRefreshTick();
  const ps = useSort(data?.payslips || [], PS_A, "name", "asc");

  const [closedOpen, setClosedOpen] = useState(false);
  const closing = false;
  const notifyClosed = () => setClosedOpen(true);
  const [emailOpen, setEmailOpen] = useState(false);
  const emailing = false;
  const emailPayslips = () => setEmailOpen(true);

  useEffect(() => {
    api.get(`/payroll/runs/${runId}`).then((r) => {
      setData(r.data);
      const cid = r.data?.run?.company_id;
      if (cid) api.get(`/companies/${cid}`).then((c) => setCompany(c.data)).catch(() => {});
      // Staff view: carry the company into the URL so the global company workspace header renders.
      if (cid && !location.pathname.startsWith("/client") && !new URLSearchParams(location.search).get("company")) {
        navigate(`${location.pathname}?company=${cid}`, { replace: true });
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId, tick]);

  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const { run } = data;
  const t = run.totals || {};

  return (
    <div className="w-full">
      {base === "" && run.company_id ? (
        <div data-testid="rundetail-breadcrumb" className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-muted-foreground mb-4">
          <Link to={`/companies/${run.company_id}`} className="hover:text-black transition-colors">{companyName || "Company"}</Link>
          <span>›</span>
          <Link to={`/history?company=${run.company_id}`} className="hover:text-black transition-colors">Payroll History</Link>
          <span>›</span>
          <span className="text-black">Run {run.run_number || run.pay_date}</span>
        </div>
      ) : (
        <Link to={`${base}/history`} className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-black mb-4">
          <ArrowLeft className="h-3.5 w-3.5" /> Payroll history
        </Link>
      )}

      <div className="mb-8">
        <h1 className="text-4xl font-black tracking-tight">Pay Run {run.run_number ? `· ${run.run_number}` : ""}{run.off_cycle && <span data-testid="run-off-cycle-tag" className="ml-3 align-middle text-[10px] font-mono uppercase tracking-widest border border-amber-500 text-amber-700 px-2 py-0.5">Off-cycle · {run.off_cycle_type || "other"}</span>}</h1>
        <p className="text-sm text-muted-foreground mt-1 font-mono">
          Pay date {run.pay_date} · {run.pay_period_start} → {run.pay_period_end} · {FREQ_LABELS[run.pay_frequency]} · {run.employee_count} employees
        </p>
        <div className="mt-2" data-testid="run-ruleset-indicator">
          {run.ruleset_id ? (
            <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-mono uppercase tracking-widest text-muted-foreground border border-border bg-neutral-50 px-2.5 py-1" title={`Payroll rules applied when this run was calculated (by pay date). CRA ${run.ruleset_id}${run.esa_ruleset_id ? `, Ontario ESA ${run.esa_ruleset_id}` : ""}.`}>
              <span className="text-black font-semibold">Payroll rules</span>
              <span>CRA: {run.ruleset_id}</span>
              {run.esa_ruleset_id && <span>Ontario ESA: {run.esa_ruleset_id}</span>}
            </span>
          ) : (
            <span className="inline-block text-[11px] font-mono uppercase tracking-widest text-muted-foreground border border-dashed border-border px-2.5 py-1" title="This payroll predates ruleset stamping. Its historical ruleset was not recorded and is not inferred.">
              Legacy payroll — ruleset not historically stamped
            </span>
          )}
        </div>
        <div className="mt-2" data-testid="run-hours-source">
          {run.hours_source ? (
            <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-mono uppercase tracking-widest text-muted-foreground border border-border bg-neutral-50 px-2.5 py-1">
              <span className="text-black font-semibold">Payroll hours source</span>
              <span>{run.hours_source === "approved_time" ? "Approved PostWage Time & Leave" : "Manual Payroll Entry"}</span>
              {run.hours_source_overridden && <span className="text-amber-700">Run override{run.hours_source_reason ? ` · ${run.hours_source_reason}` : ""}</span>}
            </span>
          ) : (
            <span className="inline-block text-[11px] font-mono uppercase tracking-widest text-muted-foreground border border-dashed border-border px-2.5 py-1" title="This payroll predates hours-source stamping. Its source was not recorded and is not inferred.">
              Historical payroll — source not recorded
            </span>
          )}
        </div>
        <div className="mt-4 flex flex-wrap gap-2" data-testid="run-actions">
          <a data-testid="run-summary-pdf-button" href={pdfUrl(`/payroll/runs/${runId}/summary.pdf`)} target="_blank" rel="noreferrer" className={BTN}>
            <FileText className="h-4 w-4" /> Run Summary (PDF)
          </a>
          <a data-testid="bulk-paystubs-button" href={pdfUrl(`/payroll/runs/${runId}/paystubs.pdf`)} target="_blank" rel="noreferrer" className={BTN}>
            <FileDown className="h-4 w-4" /> All Pay Stubs (PDF)
          </a>
          {canRunEdit && <button data-testid="email-payslips-button" onClick={emailPayslips} disabled={emailing} className={BTN}>
            <Mail className="h-4 w-4" /> {emailing ? "Emailing…" : "Email Payslips"}
          </button>}
          {canRunEdit && /POSTED|PAID/.test(run.workflow_status || (run.status === "completed" ? "POSTED_LEGACY" : "")) && (
            <button data-testid="notify-closed-button" onClick={notifyClosed} disabled={closing} title={run.closed_notice_sent_at ? `Last sent ${fmtDateTime(run.closed_notice_sent_at)} by ${run.closed_notice_sent_by}` : "Email the client that payroll is processed & closed"} className={BTN}>
              <Mail className="h-4 w-4" /> {closing ? "Sending…" : run.closed_notice_sent_at ? "Resend Payroll Confirmation" : "Email: Payroll Processed – Confirmation"}
            </button>
          )}
          {emailOpen && <PayslipEmailDialog runId={runId} onClose={() => setEmailOpen(false)} onSent={() => api.get(`/payroll/runs/${runId}`).then((rr) => setData(rr.data)).catch(() => {})} />}
          {closedOpen && <ClosedNoticeDialog runId={runId} onClose={() => setClosedOpen(false)} onSent={() => api.get(`/payroll/runs/${runId}`).then((rr) => setData(rr.data)).catch(() => {})} />}
          <a data-testid="run-holiday-audit-button" href={pdfUrl(`/payroll/runs/${runId}/holiday-report.pdf`)} target="_blank" rel="noreferrer" className={BTN}>
            <FileText className="h-4 w-4" /> Holiday Pay Audit (PDF)
          </a>
          <a data-testid="run-overtime-audit-button" href={pdfUrl(`/payroll/runs/${runId}/overtime-report.pdf`)} target="_blank" rel="noreferrer" className={BTN}>
            <FileText className="h-4 w-4" /> Overtime Audit (PDF)
          </a>
          <a data-testid="run-journal-csv-button" href={pdfUrl(`/payroll/runs/${runId}/journal`)} target="_blank" rel="noreferrer" title="QuickBooks Online journal-entry import (CSV)" className={BTN}>
            <BookText className="h-4 w-4" /> Journal Entry (QBO)
          </a>
          <a data-testid="run-journal-xlsx-button" href={pdfUrl(`/payroll/runs/${runId}/journal.xlsx`)} target="_blank" rel="noreferrer" className={BTN}>
            <BookText className="h-4 w-4" /> Journal Entry (Excel)
          </a>
        </div>
        {run.closed_notice_sent_at && (
          <div className="mt-4 border border-emerald-200 bg-emerald-50/60 px-4 py-3 text-xs" data-testid="closed-notice-receipt">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="font-mono uppercase tracking-widest text-[10px] text-emerald-800">Payroll confirmation sent</span>
              <span className="font-mono tabular-nums">{fmtDateTime(run.closed_notice_sent_at)}</span>
              <span>by {run.closed_notice_sent_by}</span>
              {run.closed_notice_pad_date && <span>· PAD date {run.closed_notice_pad_date}</span>}
            </div>
            <ul className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5">
              {(run.closed_notice_recipients || []).map((r) => {
                const email = typeof r === "string" ? r : r.email;
                const name = typeof r === "string" ? "" : r.name;
                const bounce = (run.closed_notice_failed || []).find((f) => f.email === email);
                return <li key={email} data-testid={`closed-notice-recipient-${email}`} className={bounce ? "text-red-700" : "text-emerald-900"} title={bounce ? bounce.error : "Delivered to provider"}>
                  {name ? `${name} · ` : ""}<span className="font-mono">{email}</span>{bounce ? " — bounced" : " ✓"}</li>;
              })}
            </ul>
          </div>
        )}
      </div>

      <details className="mb-6 group" data-testid="workflow-section">
        <summary className={SUMMARY}><span>Payroll workflow <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{run.workflow_status || (run.status === "completed" ? "POSTED_LEGACY" : "DRAFT")}</span></span><span className="group-open:rotate-180 transition-transform">▾</span></summary>
        <div className="mt-3">
      <RunWorkflowPanel run={run} onChanged={() => api.get(`/payroll/runs/${runId}`).then((r) => setData(r.data)).catch(() => {})} />
        </div>
      </details>

      {company && run.posted && !run.is_reversal && <RunDirectDeposit company={company} runId={runId} readOnly={!isStaff} />}

      <PayrollExceptionsPanel runId={runId} />

      {(() => {
        const cheques = (data.payslips || []).filter((p) => String(p.pay_method || "").toLowerCase().startsWith("manual"));
        if (!cheques.length) return null;
        const total = cheques.reduce((s, c) => s + (c.net_pay || 0), 0);
        return (
          <details data-testid="cheque-list" className="border border-amber-300 bg-amber-50 mb-6 px-5 py-4 group">
            <summary className="flex items-center gap-2 cursor-pointer list-none">
              <Banknote className="h-4 w-4 text-amber-700" />
              <h3 className="font-bold tracking-tight text-amber-900">Manual cheque list</h3>
              <span className="text-[10px] font-mono uppercase tracking-widest bg-amber-200 text-amber-800 px-2 py-1">{cheques.length} · {money(total)}</span>
              <span className="ml-auto text-amber-800 group-open:rotate-180 transition-transform">▾</span>
            </summary>
            <div className="mt-2">
            <p className="text-xs text-amber-800 mb-2">These employees are paid manually by the client and are not sent by direct deposit.</p>
            <div className="divide-y divide-amber-200">
              {cheques.map((c) => (
                <div key={c.payslip_id} data-testid={`cheque-${c.payslip_id}`} className="flex items-center justify-between py-1.5 text-sm text-amber-900">
                  <span>{c.employee_name}</span><span className="tabular-nums font-medium">{money(c.net_pay)}</span>
                </div>
              ))}
            </div>
            </div>
          </details>
        );
      })()}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Summary label="Gross" value={money(t.gross)} />
        <Summary label="Total Deductions" value={money(t.total_deductions)} accent="text-red-600" />
        <Summary label="Net Pay" value={money(t.net_pay)} accent="text-emerald-600" />
        <Summary label="Employer Remit (CPP+EI)" value={money((t.employer_cpp || 0) + (t.employer_ei || 0))} />
      </div>

      <details className="border border-border bg-white group" data-testid="pay-statements-section">
        <summary className="px-5 py-4 border-b border-border cursor-pointer list-none flex items-center justify-between hover:bg-neutral-50">
          <h2 className="font-bold tracking-tight">Pay Statements <span className="ml-2 text-xs font-mono text-muted-foreground">{(data.payslips || []).length} employees</span></h2>
          <span className="group-open:rotate-180 transition-transform">▾</span>
        </summary>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                <th className="px-4 py-3 sticky left-0 bg-white"><SortBtn label="Employee" col="name" sort={ps.sort} onSort={ps.toggle} /></th>
                <th className="px-4 py-3 text-right"><SortBtn label="Gross" col="gross" sort={ps.sort} onSort={ps.toggle} /></th>
                <th className="px-4 py-3 text-right"><SortBtn label="Fed" col="fed" sort={ps.sort} onSort={ps.toggle} /></th>
                <th className="px-4 py-3 text-right"><SortBtn label="ON" col="on" sort={ps.sort} onSort={ps.toggle} /></th>
                <th className="px-4 py-3 text-right"><SortBtn label="CPP" col="cpp" sort={ps.sort} onSort={ps.toggle} /></th>
                <th className="px-4 py-3 text-right"><SortBtn label="CPP2" col="cpp2" sort={ps.sort} onSort={ps.toggle} /></th>
                <th className="px-4 py-3 text-right"><SortBtn label="EI" col="ei" sort={ps.sort} onSort={ps.toggle} /></th>
                <th className="px-4 py-3 text-right"><SortBtn label="Net" col="net" sort={ps.sort} onSort={ps.toggle} /></th>
                <th className="px-4 py-3 text-right"><SortBtn label="YTD Gross" col="ytd_gross" sort={ps.sort} onSort={ps.toggle} /></th>
                <th className="px-4 py-3 text-right"><SortBtn label="YTD Net" col="ytd_net" sort={ps.sort} onSort={ps.toggle} /></th>
                <th className="px-4 py-3 text-right">Stub</th>
              </tr>
            </thead>
            <tbody>
              {ps.sorted.map((p) => {
                const coded = [...(p.custom_earnings || []).map((l) => ({ ...l, _kind: "earn" })), ...(p.custom_deductions || []).map((l) => ({ ...l, _kind: "ded" }))];
                const isOpen = !!expanded[p.payslip_id];
                return (
                <Fragment key={p.payslip_id}>
                <tr data-testid={`payslip-row-${p.payslip_id}`} className="border-b border-border last:border-0 hover:bg-muted/50">
                  <td className="px-4 py-3 sticky left-0 bg-white font-medium">
                    {coded.length > 0 ? (
                      <button data-testid={`toggle-lines-${p.payslip_id}`} onClick={() => setExpanded((x) => ({ ...x, [p.payslip_id]: !x[p.payslip_id] }))} className="inline-flex items-center gap-1.5 hover:underline">
                        <span className="text-muted-foreground text-xs">{isOpen ? "▾" : "▸"}</span>{p.employee_name}
                        <span className="text-[9px] font-mono uppercase border border-border text-muted-foreground px-1 py-0.5">{coded.length} coded</span>
                        {p.non_cash_taxable > 0 && <span title="Taxable income includes a non-cash benefit not paid as cash — this is why taxable/gross exceeds net cash." data-testid={`noncash-flag-${p.payslip_id}`} className="text-[9px] font-mono uppercase border border-violet-500 bg-violet-50 text-violet-700 px-1 py-0.5">+{money(p.non_cash_taxable)} non-cash</span>}
                      </button>
                    ) : p.employee_name}
                    <DepositBadge d={p.deposit} id={p.payslip_id} />
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{money(p.gross)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-red-600">{money(p.federal_tax)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-red-600">{money(p.provincial_tax)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-red-600">{money(p.cpp)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-red-600">{money(p.cpp2)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-red-600">{money(p.ei)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-emerald-600 font-medium">{money(p.net_pay)}</td>
                  <td data-testid={`ytd-gross-${p.payslip_id}`} className="px-4 py-3 text-right tabular-nums text-muted-foreground">{money(p.ytd_gross)}</td>
                  <td data-testid={`ytd-net-${p.payslip_id}`} className="px-4 py-3 text-right tabular-nums text-muted-foreground">{money(p.ytd_net)}</td>
                  <td className="px-4 py-3 text-right">
                    <a data-testid={`paystub-pdf-${p.payslip_id}`} href={pdfUrl(`/payroll/paystub/${p.payslip_id}`)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-black">
                      <FileDown className="h-3.5 w-3.5" /> PDF
                    </a>
                  </td>
                </tr>
                {isOpen && coded.length > 0 && (
                  <tr data-testid={`coded-lines-${p.payslip_id}`} className="border-b border-border bg-neutral-50">
                    <td colSpan={11} className="px-8 py-3">
                      <div className="space-y-1">
                        {coded.map((l, i) => (
                          <div key={i} data-testid={`coded-line-${p.payslip_id}-${i}`} className="flex items-center justify-between text-xs">
                            <span className="flex items-center gap-1.5">
                              <span className={`font-mono uppercase w-9 ${l._kind === "earn" ? "text-emerald-700" : "text-red-700"}`}>{l._kind}</span>
                              <span>{l.code ? `${l.code} — ` : ""}{l.name || (l.code ? "" : l._kind === "earn" ? "Earning" : "Deduction")}</span>
                              {!l.code && <span className="text-[9px] font-mono uppercase border border-border text-muted-foreground px-1 py-0.5">legacy</span>}
                              {l.code && <span className="text-[9px] font-mono uppercase border border-border text-muted-foreground px-1 py-0.5">assignment/one-time</span>}
                              {l.requires_treatment_review && <span title="Treatment may depend on CRA circumstances." className="text-[9px] font-mono uppercase border border-amber-500 bg-amber-50 text-amber-700 px-1 py-0.5">review</span>}
                              {l.non_cash && <span title="Non-cash taxable benefit: increases taxable/statutory income but is NOT paid as cash." data-testid={`noncash-badge-${p.payslip_id}-${i}`} className="text-[9px] font-mono uppercase border border-violet-500 bg-violet-50 text-violet-700 px-1 py-0.5">non-cash</span>}
                              {l.code === "REIMB" && <span title="Reimbursement: cash paid, not employment income." className="text-[9px] font-mono uppercase border border-sky-500 bg-sky-50 text-sky-700 px-1 py-0.5">reimb</span>}
                              {l.automobile && <span title={`Automobile benefit (${l.automobile.tax_year}): standby ${money(l.automobile.full_standby_charge)} · ${l.automobile.standby_method}; per-km ${l.automobile?.parameters?.operating_cost_per_km}`} className="text-[9px] font-mono uppercase border border-violet-500 bg-violet-50 text-violet-700 px-1 py-0.5">auto</span>}
                              {l.cap_reason === "ANNUAL_MAX" && <span className="text-[9px] font-mono uppercase border border-orange-500 bg-orange-50 text-orange-700 px-1 py-0.5">annual max applied</span>}
                              {l.cap_reason === "PER_PAY_MAX" && <span className="text-[9px] font-mono uppercase border border-blue-500 bg-blue-50 text-blue-700 px-1 py-0.5">per-pay max applied</span>}
                            </span>
                            <span className="tabular-nums">{l._capped_from != null && <span className="text-muted-foreground line-through mr-1">{money(l._capped_from)}</span>}{money(l.amount)}{l.gl_account ? <span className="ml-2 text-[9px] font-mono text-muted-foreground">→ {l.gl_account}</span> : null}</span>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}

function Summary({ label, value, accent }) {
  return (
    <div className="border border-border bg-white p-5">
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`mt-2 text-2xl font-black tracking-tight tabular-nums ${accent || ""}`}>{value}</div>
    </div>
  );
}
